import React, { useEffect, useState } from 'react';
import type { Dispatch } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import SmartUserPicker from '@atlaskit/smart-user-picker';
import type { OptionData } from '@atlaskit/user-picker';
import { ErrorMessage } from '@atlaskit/form';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	FilterHeaderCompiled,
	InputContainerCompiled,
	SelectContainerCompiled,
} from './presentationalComponents.tsx';
import type { FiltersState } from './useFiltersReducer.ts';
import { FilterKeys } from './filtersDefinitions.ts';

const i18n = defineMessages({
	assigneeLabel: {
		id: 'home.views.tasks.filters.label.assignee',
		defaultMessage: 'Assignee',
		description: 'The label for a selector to filter tasks by the assignee.',
	},

	assigneePlaceholderMessage: {
		id: 'home.views.tasks.filters.assignee.placeholder',
		defaultMessage: 'Search or select by name',
		description: 'The placeholder message for the user picker',
	},

	assigneeMaxUsersLabel: {
		id: 'home.views.tasks.filters.label.assignee.max.users',
		defaultMessage: 'Maximum reached',
		description: 'The label displayed when the maximum number of users has been selected',
	},

	assigneeNumberUsersLabel: {
		id: 'home.views.tasks.filters.label.assignee.number.users',
		defaultMessage: 'add up to 10 people',
		description: 'The placeholder message displayed once one user has been added to the filter',
	},
});

const MAX_SELECTED_USERS = 10;
interface SelectUserAssigneeProps {
	filterDispatch: Dispatch<any>;
	filterState: FiltersState;
}

export const SelectAssignee = ({ filterDispatch, filterState }: SelectUserAssigneeProps) => {
	const { formatMessage } = useIntl();
	const [selectedUsers, setSelectedUsers] = useState<OptionData[]>([]);
	const [disableInput, setDisableInput] = useState<boolean>(false);
	const { cloudId, userId } = useSessionData();

	useEffect(() => {
		if (filterState.filters.assignee) {
			setSelectedUsers(filterState.filters[FilterKeys.ASSIGNEE] || []);
			setDisableInput(filterState.filters.assignee.length >= MAX_SELECTED_USERS);
		} else {
			setSelectedUsers([]);
			setDisableInput(false);
		}
		const input = document.getElementById('react-select-contributors-input');
		if (input) {
			input.style.width = '110%';
		}
	}, [filterState]);

	const onChange = (contributors) => {
		if (contributors) {
			setSelectedUsers(contributors);
			setDisableInput(contributors.length >= MAX_SELECTED_USERS);
		}
	};

	const onBlur = () => {
		filterDispatch({
			type: FilterKeys.ASSIGNEE,
			payload: selectedUsers,
		});
	};

	return (
		<InputContainerCompiled>
			<FilterHeaderCompiled htmlFor="assignee-filter">
				{formatMessage(i18n.assigneeLabel)}
			</FilterHeaderCompiled>
			<SelectContainerCompiled>
				<SmartUserPicker
					fieldId="contributors"
					isMulti
					maxOptions={10}
					onChange={onChange}
					onBlur={onBlur}
					principalId={userId ?? undefined}
					productKey="confluence"
					siteId={cloudId}
					placeholder={formatMessage(i18n.assigneePlaceholderMessage)}
					value={selectedUsers}
					noBorder
					addMoreMessage={disableInput ? '' : formatMessage(i18n.assigneeNumberUsersLabel)}
					disableInput={disableInput}
					displayEmailInByline
					enableEmailSearch
					allowEmailSelectionWhenEmailMatched={false}
				/>
				{disableInput && <ErrorMessage>{formatMessage(i18n.assigneeMaxUsersLabel)}</ErrorMessage>}
			</SelectContainerCompiled>
		</InputContainerCompiled>
	);
};
